import { useMemo } from 'react'
import { Box, Typography, Paper, useTheme } from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

interface VitalDataPoint {
  id?: number
  metric: string
  value: number
  unit?: string
  date?: string
  timestamp?: string
}

interface VitalsChartProps {
  vitals: VitalDataPoint[]
  title?: string
  height?: number
  windowDays?: number
  interval?: string
}

const METRIC_CONFIG: Record<
  string,
  { label: string; unit: string; color: string; yAxisDomain?: [number, number] }
> = {
  sbp: { label: 'Systolic BP', unit: 'mmHg', color: '#e74c3c', yAxisDomain: [80, 200] },
  dbp: { label: 'Diastolic BP', unit: 'mmHg', color: '#3498db', yAxisDomain: [40, 120] },
  hr: { label: 'Heart Rate', unit: 'bpm', color: '#9b59b6', yAxisDomain: [40, 140] },
  pulse: { label: 'Pulse', unit: 'bpm', color: '#8e44ad', yAxisDomain: [40, 140] },
  spo2: { label: 'SpO2', unit: '%', color: '#2ecc71', yAxisDomain: [85, 100] },
  oxygen_saturation: { label: 'SpO2', unit: '%', color: '#27ae60', yAxisDomain: [85, 100] },
  rr: { label: 'Resp. Rate', unit: '/min', color: '#f39c12', yAxisDomain: [8, 30] },
  respirations: { label: 'Resp. Rate', unit: '/min', color: '#e67e22', yAxisDomain: [8, 30] },
  temp: { label: 'Temperature', unit: '°F', color: '#e91e63', yAxisDomain: [95, 103] },
  temperature: { label: 'Temperature', unit: '°F', color: '#c2185b', yAxisDomain: [95, 103] },
  glucose: { label: 'Glucose', unit: 'mg/dL', color: '#00bcd4', yAxisDomain: [60, 300] },
}

const VitalsChart: React.FC<VitalsChartProps> = ({ vitals, title, height = 220, windowDays, interval }) => {
  const theme = useTheme()

  // Parse interval string to milliseconds
  const parseInterval = (intervalStr?: string): number | null => {
    if (!intervalStr) return null
    const match = intervalStr.match(/^(\d+)([smhd])$/)
    if (!match) return null
    const [, num, unit] = match
    const value = parseInt(num, 10)
    const multipliers: Record<string, number> = {
      s: 1000,
      m: 60 * 1000,
      h: 60 * 60 * 1000,
      d: 24 * 60 * 60 * 1000,
    }
    return value * (multipliers[unit] || 0)
  }

  // Transform data for recharts
  const chartData = useMemo(() => {
    const metricsInData = new Set<string>()
    
    // Group by timestamp
    const groupedData = vitals.reduce((acc: Record<string, any>, vital: VitalDataPoint) => {
      const timestamp = vital.date || vital.timestamp || ''
      const metric = vital.metric.toLowerCase()
      metricsInData.add(metric)
      
      if (!acc[timestamp]) {
        acc[timestamp] = { timestamp, timestampMs: new Date(timestamp).getTime() }
      }
      
      acc[timestamp][metric] = vital.value
      
      return acc
    }, {})
    
    // Sort by timestamp
    const sorted = Object.values(groupedData).sort((a: any, b: any) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )

    // If a windowDays prop is provided, filter to that sliding window
    let filtered = sorted
    if (windowDays && windowDays > 0) {
      const cutoff = Date.now() - windowDays * 24 * 60 * 60 * 1000
      filtered = sorted.filter((row: any) => {
        const t = new Date(row.timestamp).getTime()
        return !Number.isNaN(t) && t >= cutoff
      })

      // ensure we still show the most recent data point even if it's outside the window
      if (filtered.length === 0 && sorted.length > 0) {
        // include last N points (up to 20) to show recent activity
        filtered = sorted.slice(-20)
      }
    }
    // use filtered data for subsequent calculations
    const finalData = filtered

    // Compute global min/max across all metrics so Y axis fits tightly
    const allValues: number[] = []
    for (const row of finalData) {
      for (const m of metricsInData) {
        const v = row[m]
        if (v !== undefined && v !== null && !Number.isNaN(Number(v))) {
          allValues.push(Number(v))
        }
      }
    }

    // Defaults
    let domain: [number, number] = [0, 0]
    let ticks: number[] = []

    if (allValues.length > 0) {
      const min = Math.min(...allValues)
      const max = Math.max(...allValues)

      // Preserve 10% of the minimum value at the bottom
      const bottomPad = Math.abs(min) * 0.1
      const topPad = Math.abs(min) * 0.1

      const domainMinRaw = min - bottomPad
      const domainMaxRaw = max + topPad

      // Round domain to multiples of 10: floor for min, ceil for max
      const domainMin = Math.floor(domainMinRaw / 10) * 10
      const domainMax = Math.ceil(domainMaxRaw / 10) * 10

      // Ensure sensible span
      let span = domainMax - domainMin
      if (span <= 0) {
        // fallback to a small span
        span = Math.max(10, Math.abs(domainMax) * 0.1 || 10)
      }

      // Choose tick interval as a multiple of 10 so ticks are integers and evenly spaced
      const desiredTicks = 5
      const rawInterval = span / (desiredTicks - 1)
      const interval = Math.max(10, Math.ceil(rawInterval / 10) * 10)

      // Recompute ticks start/end aligned to interval
      const ticksStart = Math.floor(domainMin / interval) * interval
      const ticksEnd = Math.ceil(domainMax / interval) * interval

      // Build ticks array
      for (let t = ticksStart; t <= ticksEnd; t += interval) {
        ticks.push(t)
      }

      domain = [ticksStart, ticksEnd]
    }

    // Calculate X-axis ticks based on interval (max 6 ticks)
    let xTicks: number[] = []
    const intervalMs = parseInterval(interval)
    if (finalData.length > 0) {
      const timeValues = finalData.map((d: any) => d.timestampMs).filter((t: number) => !isNaN(t))
      if (timeValues.length > 0) {
        const minTime = Math.min(...timeValues)
        const maxTime = Math.max(...timeValues)
        
        if (intervalMs && intervalMs > 0) {
          // Use the schedule interval for ticks, but limit to max 6
          const timeRange = maxTime - minTime
          const maxTicks = 6
          let tickInterval = intervalMs
          
          // If we'd have too many ticks, increase the interval
          const estimatedTicks = Math.floor(timeRange / tickInterval) + 1
          if (estimatedTicks > maxTicks) {
            tickInterval = Math.ceil(timeRange / (maxTicks - 1))
          }
          
          // Generate ticks
          for (let t = minTime; t <= maxTime; t += tickInterval) {
            xTicks.push(t)
            if (xTicks.length >= maxTicks) break
          }
          // Ensure last point is included
          if (xTicks[xTicks.length - 1] < maxTime) {
            xTicks.push(maxTime)
          }
        } else {
          // No interval specified, use auto ticks (max 6)
          const numTicks = Math.min(6, finalData.length)
          const step = Math.max(1, Math.floor((finalData.length - 1) / (numTicks - 1)))
          for (let i = 0; i < finalData.length; i += step) {
            xTicks.push(finalData[i].timestampMs)
          }
          // Ensure last point
          if (xTicks[xTicks.length - 1] !== maxTime) {
            xTicks.push(maxTime)
          }
        }
      }
    }

    return { data: finalData, metrics: Array.from(metricsInData), domain, ticks, xTicks }
  }, [vitals, windowDays, interval])

  // Format timestamp for display
  const formatTimestamp = (timestampMs: number) => {
    try {
      const date = new Date(timestampMs)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return String(timestampMs)
    }
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null

    return (
      <Paper sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
          {formatTimestamp(label)}
        </Typography>
        {payload.map((entry: any) => {
          const config = METRIC_CONFIG[entry.dataKey] || { label: entry.dataKey, unit: '', color: entry.color }
          return (
            <Typography key={entry.dataKey} variant="caption" display="block" color={entry.color}>
              {config.label}: <strong>{entry.value}</strong> {config.unit}
            </Typography>
          )
        })}
      </Paper>
    )
  }

  if (vitals.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="text.secondary">No vitals data to display</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          {/* remove background grid lines for a cleaner look */}
          <XAxis
            dataKey="timestampMs"
            type="number"
            domain={['dataMin', 'dataMax']}
            ticks={chartData.xTicks.length > 0 ? chartData.xTicks : undefined}
            tickFormatter={formatTimestamp}
            stroke={theme.palette.text.secondary}
            style={{ fontSize: '0.75rem' }}
          />
          <YAxis domain={chartData.domain || ['auto', 'auto']} ticks={chartData.ticks} tickFormatter={(v) => Number.isInteger(v) ? String(v) : String(Math.round(v))} stroke={theme.palette.text.secondary} style={{ fontSize: '0.75rem' }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '0.75rem' }}
            formatter={(value: string) => METRIC_CONFIG[value]?.label || value}
          />
          {chartData.metrics.map((metric: string) => {
            const config = METRIC_CONFIG[metric] || { color: '#95a5a6' }
            const strokeColor = theme.palette.mode === 'dark' ? theme.palette.text.primary : config.color
            return (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={strokeColor}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            )
          })}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default VitalsChart
